import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Progress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@repo/ui";
import { Dialog } from "@repo/ui";
import { useAction, useQuery } from "convex/react";
import {
  AlertTriangle,
  ArrowLeft,
  BarChart3,
  Download,
  List,
  Loader2,
  Mail,
  UserPlus,
} from "lucide-react";
import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";
import { api } from "../../../../convex/_generated/api";
import type { Id } from "../../../../convex/_generated/dataModel";
import { StatutCampagneEnum } from "../../../../convex/schema";
import { calculerScores, type EntStructQuestionEtat } from "@repo/utils";
import { ArrowUp, ArrowDown } from "lucide-react";
import { AjouterCandidatsDialog } from "./AjouterCandidatsDialog";
import { InbasketCampagneTable } from "./results/InbasketCampagneTable";

type TabValue = "parcours" | "comparaison";

type EvaluationData = {
  _id: Id<"evaluations">;
  parcoursId: Id<"parcoursEvaluations">;
  nomCandidat: string;
  prenomCandidat: string;
  dateFin?: number;
  questionsEtat?: EntStructQuestionEtat[];
  isSuspect: boolean;
};

type EvaluationWithScores = EvaluationData & {
  scores: ReturnType<typeof calculerScores> | null;
  globalPercentage: number;
};

type SortConfig = {
  column: string;
  direction: "asc" | "desc";
};

function getScoreColor(percentage: number): string {
  if (percentage >= 70) return "bg-green-100 text-green-800";
  if (percentage >= 50) return "bg-yellow-100 text-yellow-800";
  return "bg-red-100 text-red-800";
}

export function CampagneDetail() {
  const { id } = useParams<{ id: string }>();
  const campagneId = id as Id<"campagnes">;

  const [activeTab, setActiveTab] = useState<TabValue>("parcours");
  const [sendingEmails, setSendingEmails] = useState(false);
  const [showAddCandidats, setShowAddCandidats] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [selectedParcoursIds, setSelectedParcoursIds] = useState<
    Id<"parcoursEvaluations">[]
  >([]);
  const [sortConfig, setSortConfig] = useState<SortConfig>({
    column: "dateFin",
    direction: "desc",
  });

  const envoyerLiensParcoursSelectionnes = useAction(
    api.email.envoyerLiensParcoursSelectionnes,
  );

  const campagne = useQuery(api.campagnes.obtenirCampagne, { campagneId });
  const stats = useQuery(api.campagnes.obtenirStatsCampagne, { campagneId });

  const isInbasketCampagne = campagne?.evaluationsConfig?.some(
    (e) => e.typeEval === "inbasket",
  ) ?? false;
  const isEntStructCampagne = campagne?.evaluationsConfig?.some(
    (e) => e.typeEval === "ent-struct",
  ) ?? false;

  const evaluations = useQuery(
    api.evaluations.obtenirEvaluationsEntStructParCampagne,
    isEntStructCampagne ? { campagneId } : "skip",
  );
  const evaluationsInbasket = useQuery(
    api.evaluations.obtenirEvaluationsInbasketParCampagne,
    isInbasketCampagne ? { campagneId } : "skip",
  );

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const getStatutBadge = (statut: string) => {
    switch (statut) {
      case StatutCampagneEnum.ACTIVE:
        return <Badge variant="success">Active</Badge>;
      case StatutCampagneEnum.TERMINEE:
        return <Badge variant="secondary">Terminée</Badge>;
      case StatutCampagneEnum.BROUILLON:
        return <Badge variant="outline">Brouillon</Badge>;
      default:
        return <Badge variant="outline">{statut}</Badge>;
    }
  };

  const toggleParcours = (parcoursId: Id<"parcoursEvaluations">) => {
    setSelectedParcoursIds((prev) =>
      prev.includes(parcoursId)
        ? prev.filter((id) => id !== parcoursId)
        : [...prev, parcoursId],
    );
  };

  const toggleAllParcours = () => {
    if (!stats) return;
    if (selectedParcoursIds.length === stats.parcours.length) {
      setSelectedParcoursIds([]);
    } else {
      setSelectedParcoursIds(stats.parcours.map((p) => p._id));
    }
  };

  const handleSendEmails = async () => {
    if (selectedParcoursIds.length === 0) return;
    setSendingEmails(true);
    try {
      const res = await envoyerLiensParcoursSelectionnes({
        campagneId,
        parcoursIds: selectedParcoursIds,
      });
      alert(res.message);
      setShowEmailModal(false);
      setSelectedParcoursIds([]);
    } catch (err) {
      alert(`Erreur: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setSendingEmails(false);
    }
  };

  // --- Comparison logic ---

  const evaluationsWithScores = useMemo(() => {
    if (!evaluations) return [];
    return evaluations.map(
      (evaluation: EvaluationData): EvaluationWithScores => {
        const scores = evaluation.questionsEtat
          ? calculerScores(evaluation.questionsEtat)
          : null;

        return {
          ...evaluation,
          scores,
          globalPercentage:
            scores && scores.scoreGlobalMax > 0
              ? Math.round(
                  (scores.scoreGlobalObtenu / scores.scoreGlobalMax) * 100,
                )
              : 0,
        };
      },
    );
  }, [evaluations]);

  const allCompetences = useMemo(() => {
    if (evaluationsWithScores.length === 0) return [];
    const firstEvalWithScores = evaluationsWithScores.find((e) => e.scores);
    if (!firstEvalWithScores?.scores) return [];
    return firstEvalWithScores.scores.competences.filter(
      (c) => c.nom !== "Question neutre",
    );
  }, [evaluationsWithScores]);

  const allCompetencesForExport = useMemo(() => {
    if (evaluationsWithScores.length === 0) return [];
    const firstEvalWithScores = evaluationsWithScores.find((e) => e.scores);
    if (!firstEvalWithScores?.scores) return [];
    return firstEvalWithScores.scores.competences;
  }, [evaluationsWithScores]);

  const handleExportCSV = () => {
    if (!campagne || evaluationsWithScores.length === 0) return;

    // 1. On modifie les headers pour avoir "Nom - Score" et "Nom - Max"
    const headers = [
      "Candidat",
      "Date",
      ...allCompetencesForExport.flatMap((c) => [
        `${c.nom} (Obtenu)`,
        `${c.nom} (Max)`,
      ]),
      "Score Global (%)",
    ];

    const rows = sortedEvaluations.map((evaluation) => {
      const scoresMap = new Map(
        evaluation.scores?.competences.map((c) => [c.nom, c]) ?? [],
      );

      return [
        `${evaluation.prenomCandidat} ${evaluation.nomCandidat}`,
        evaluation.dateFin ? formatDate(evaluation.dateFin) : "",
        // 2. On utilise flatMap ici aussi pour injecter deux cellules par compétence
        ...allCompetencesForExport.flatMap((comp) => {
          const score = scoresMap.get(comp.nom);
          return score
            ? [String(score.scoreObtenu), String(score.scoreMax)] // Deux valeurs distinctes
            : ["", ""]; // Deux cellules vides si pas de score
        }),
        `${evaluation.globalPercentage}`,
      ];
    });

    const csvContent = [
      headers.join(";"),
      ...rows.map((row) =>
        row
          .map((cell) => {
            const cellStr = String(cell); // On s'assure que c'est un string
            return cellStr.includes(";")
              ? `"${cellStr.replace(/"/g, '""')}"`
              : cellStr;
          })
          .join(";"),
      ),
    ].join("\n");

    const BOM = "\uFEFF";
    const blob = new Blob([BOM + csvContent], {
      type: "text/csv;charset=utf-8;",
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    const dateStr = new Date().toISOString().slice(0, 10);
    const safeName = campagne.nom.replace(/[^a-zA-Z0-9]/g, "_");
    link.href = url;
    link.download = `${safeName}_${dateStr}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleSort = (column: string) => {
    setSortConfig((prev: SortConfig) => {
      if (prev.column === column) {
        return { column, direction: prev.direction === "asc" ? "desc" : "asc" };
      }
      return { column, direction: "asc" };
    });
  };

  const sortedEvaluations = useMemo(() => {
    const sorted = [...evaluationsWithScores];
    const { column, direction } = sortConfig;

    sorted.sort((a: EvaluationWithScores, b: EvaluationWithScores) => {
      let aValue: number | string | undefined;
      let bValue: number | string | undefined;

      switch (column) {
        case "nomCandidat":
          aValue = a.nomCandidat;
          bValue = b.nomCandidat;
          break;
        case "prenomCandidat":
          aValue = a.prenomCandidat;
          bValue = b.prenomCandidat;
          break;
        case "dateFin":
          aValue = a.dateFin;
          bValue = b.dateFin;
          break;
        case "globalPercentage":
          aValue = a.globalPercentage;
          bValue = b.globalPercentage;
          break;
        default:
          if (a.scores && b.scores) {
            const aCompScore = a.scores.competences.find(
              (c) => c.nom === column,
            );
            const bCompScore = b.scores.competences.find(
              (c) => c.nom === column,
            );
            aValue = aCompScore?.scoreObtenu ?? 0;
            bValue = bCompScore?.scoreObtenu ?? 0;
          }
      }

      if (aValue === undefined || aValue === null)
        return direction === "asc" ? 1 : -1;
      if (bValue === undefined || bValue === null)
        return direction === "asc" ? -1 : 1;

      if (typeof aValue === "string" && typeof bValue === "string") {
        const comparison = aValue.localeCompare(bValue);
        return direction === "asc" ? comparison : -comparison;
      }

      if (typeof aValue === "number" && typeof bValue === "number") {
        return direction === "asc" ? aValue - bValue : bValue - aValue;
      }

      return 0;
    });

    return sorted;
  }, [evaluationsWithScores, sortConfig]);

  // --- Loading ---

  if (campagne === undefined || stats === undefined) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  if (campagne === null || stats === null) {
    return (
      <div className="min-h-screen flex flex-col items-start">
        <div className="w-full container mx-auto p-8">
          <Link
            to="/campagnes"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour aux campagnes
          </Link>
          <Card>
            <CardContent className="text-center py-8">
              <p className="text-muted-foreground">Campagne introuvable</p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-start">
      <div className="w-full container mx-auto p-8">
        {/* Header */}
        <div className="mb-6">
          <Link
            to="/campagnes"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour aux campagnes
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">{campagne.nom}</h1>
              <div className="flex items-center gap-3 mt-2">
                {getStatutBadge(campagne.statut)}
                <span className="text-sm text-muted-foreground">
                  {stats.nbCandidats} candidat(s)
                </span>
                {campagne.dateDebut && (
                  <span className="text-sm text-muted-foreground">
                    Début : {formatDate(campagne.dateDebut)}
                  </span>
                )}
                {campagne.dateFin && (
                  <span className="text-sm text-muted-foreground">
                    Fin : {formatDate(campagne.dateFin)}
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowAddCandidats(true)}
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Ajouter des candidats
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={!stats || stats.parcours.length === 0}
                onClick={() => {
                  setSelectedParcoursIds([]);
                  setShowEmailModal(true);
                }}
              >
                <Mail className="h-4 w-4 mr-2" />
                Envoyer les liens
              </Button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex items-center gap-1 mb-6 border-b">
          <button
            onClick={() => setActiveTab("parcours")}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "parcours"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <List className="h-4 w-4" />
            Parcours
          </button>
          <button
            onClick={() => setActiveTab("comparaison")}
            className={`flex items-center gap-2 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "comparaison"
                ? "border-primary text-primary"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            <BarChart3 className="h-4 w-4" />
            Résultats
          </button>
        </div>

        {/* Tab: Parcours */}
        {activeTab === "parcours" && (
          <Card>
            <CardContent className="p-0">
              <Table className="w-full table-fixed">
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-[25%]">Candidat</TableHead>
                    <TableHead className="w-[25%]">Email</TableHead>
                    <TableHead className="w-[20%]">Progression</TableHead>
                    <TableHead className="w-[15%]">Statut</TableHead>
                    <TableHead className="w-[15%] text-right">
                      Actions
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats.parcours.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} className="text-center py-8">
                        <p className="text-muted-foreground">
                          Aucun parcours dans cette campagne
                        </p>
                      </TableCell>
                    </TableRow>
                  ) : (
                    stats.parcours.map((p) => (
                      <TableRow
                        key={p._id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => (window.location.href = `/p/${p._id}?from=/campagnes/${campagneId}`)}
                      >
                        <TableCell className="font-medium">
                          {p.prenomCandidat} {p.nomCandidat}
                        </TableCell>
                        <TableCell>{p.emailCandidat}</TableCell>
                        <TableCell>
                          <div className="min-w-[100px]">
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span>{p.progression}%</span>
                            </div>
                            <Progress value={p.progression} className="h-2" />
                          </div>
                        </TableCell>
                        <TableCell>
                          {p.statutGlobal === "termine" && (
                            <Badge variant="success">Terminé</Badge>
                          )}
                          {p.statutGlobal === "en_cours" && (
                            <Badge variant="warning">En cours</Badge>
                          )}
                          {p.statutGlobal === "non_commence" && (
                            <Badge variant="purple">Non commencé</Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-right">
                          <Link
                            to={`/p/${p._id}?from=/campagnes/${campagneId}`}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Button variant="ghost" size="sm">
                              Voir
                            </Button>
                          </Link>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Tab: Résultats */}
        {activeTab === "comparaison" && isInbasketCampagne && (
          <div className="mb-6">
            {isEntStructCampagne && (
              <h2 className="text-base font-semibold mb-3">In-Basket</h2>
            )}
            {evaluationsInbasket === undefined ? (
              <Card>
                <CardContent className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <span className="ml-2">Chargement des évaluations...</span>
                </CardContent>
              </Card>
            ) : (
              <InbasketCampagneTable evaluations={evaluationsInbasket} />
            )}
          </div>
        )}

        {activeTab === "comparaison" && isEntStructCampagne && (
          <div className="mb-6">
            {isInbasketCampagne && (
              <h2 className="text-base font-semibold mb-3">Entretien structuré</h2>
            )}
          <>
            {evaluations === undefined ? (
              <Card>
                <CardContent className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-primary" />
                  <span className="ml-2">Chargement des évaluations...</span>
                </CardContent>
              </Card>
            ) : evaluations.length === 0 ? (
              <Card>
                <CardContent className="text-center py-8">
                  <p className="text-muted-foreground">
                    Aucune évaluation d'entretien structuré terminée dans cette
                    campagne.
                  </p>
                </CardContent>
              </Card>
            ) : (
              <>
                <div className="mb-4 flex items-center justify-between">
                  <p className="text-sm text-muted-foreground">
                    {evaluations.length} candidat(s) évalué(s) en entretien
                    structuré
                  </p>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleExportCSV}
                    disabled={evaluationsWithScores.length === 0}
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Exporter CSV
                  </Button>
                </div>
                <Card>
                  <CardContent>
                    <div className="mb-4">
                      <p className="text-sm text-muted-foreground">
                        Cliquer sur l'en-tête pour trier
                      </p>
                    </div>

                    <div className="overflow-x-auto">
                      <Table className="table-fixed w-full min-w-[120px]">
                        <colgroup>
                          <col className="w-[120px]" />
                          <col className="w-[120px]" />
                          <col className="w-10" />
                          {allCompetences.map((comp) => (
                            <col key={comp.nom} className="w-[120px]" />
                          ))}
                          <col className="w-[120px]" />
                          <col className="w-[120px]" />
                        </colgroup>
                        <TableHeader>
                          <TableRow>
                            <TableHead
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => handleSort("prenomCandidat")}
                            >
                              <div className="flex items-center gap-1">
                                Candidat
                                {sortConfig.column === "prenomCandidat" &&
                                  (sortConfig.direction === "asc" ? (
                                    <ArrowUp className="h-4 w-4" />
                                  ) : (
                                    <ArrowDown className="h-4 w-4" />
                                  ))}
                              </div>
                            </TableHead>
                            <TableHead
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => handleSort("dateFin")}
                            >
                              <div className="flex items-center gap-1">
                                Date
                                {sortConfig.column === "dateFin" &&
                                  (sortConfig.direction === "asc" ? (
                                    <ArrowUp className="h-4 w-4" />
                                  ) : (
                                    <ArrowDown className="h-4 w-4" />
                                  ))}
                              </div>
                            </TableHead>
                            <TableHead />

                            {allCompetences.map((comp) => (
                              <TableHead
                                key={comp.nom}
                                className="cursor-pointer hover:bg-muted/50"
                                onClick={() => handleSort(comp.nom)}
                              >
                                <div className="flex flex-col items-start gap-1">
                                  <span className="text-left break-words hyphens-auto">
                                    {comp.nom}
                                  </span>
                                  {sortConfig.column === comp.nom &&
                                    (sortConfig.direction === "asc" ? (
                                      <ArrowUp className="h-4 w-4" />
                                    ) : (
                                      <ArrowDown className="h-4 w-4" />
                                    ))}
                                </div>
                              </TableHead>
                            ))}

                            <TableHead
                              className="cursor-pointer hover:bg-muted/50"
                              onClick={() => handleSort("globalPercentage")}
                            >
                              <div className="flex items-center gap-1">
                                Score Global
                                {sortConfig.column === "globalPercentage" &&
                                  (sortConfig.direction === "asc" ? (
                                    <ArrowUp className="h-4 w-4" />
                                  ) : (
                                    <ArrowDown className="h-4 w-4" />
                                  ))}
                              </div>
                            </TableHead>
                            <TableHead className="text-center">
                              Actions
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {sortedEvaluations.map((evaluation) => (
                            <TableRow key={evaluation._id}>
                              <TableCell className="font-medium">
                                <Link
                                  to={`/p/${evaluation.parcoursId}/evaluation/${evaluation._id}`}
                                  className="hover:underline"
                                >
                                  {evaluation.prenomCandidat}{" "}
                                  {evaluation.nomCandidat}
                                </Link>
                              </TableCell>

                              <TableCell>
                                {evaluation.dateFin
                                  ? formatDate(evaluation.dateFin)
                                  : "-"}
                              </TableCell>

                              <TableCell className="text-center">
                                {evaluation.isSuspect && (
                                  <AlertTriangle
                                    className="h-4 w-4 text-orange-500"
                                    aria-label="Comportement suspect détecté (copier-coller ou changement d'onglet)"
                                  />
                                )}
                              </TableCell>

                              {allCompetences.map((comp) => {
                                const compScore =
                                  evaluation.scores?.competences.find(
                                    (c) => c.nom === comp.nom,
                                  );
                                const scoreObtenu = compScore?.scoreObtenu ?? 0;
                                const scoreMax = compScore?.scoreMax ?? 0;
                                const percentage =
                                  scoreMax > 0
                                    ? Math.round((scoreObtenu / scoreMax) * 100)
                                    : 0;

                                return (
                                  <TableCell key={comp.nom}>
                                    <Badge
                                      className={getScoreColor(percentage)}
                                    >
                                      {scoreObtenu}/{scoreMax}
                                    </Badge>
                                  </TableCell>
                                );
                              })}

                              <TableCell>
                                <Badge
                                  className={getScoreColor(
                                    evaluation.globalPercentage,
                                  )}
                                >
                                  {evaluation.globalPercentage}%
                                </Badge>
                              </TableCell>
                              <TableCell className="text-center">
                                <Link
                                  to={`/p/${evaluation.parcoursId}/evaluation/${evaluation._id}?from=/campagnes/${campagneId}`}
                                >
                                  <Button variant="outline" size="sm">
                                    <BarChart3 className="h-4 w-4 mr-1" />
                                    Voir détails
                                  </Button>
                                </Link>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  </CardContent>
                </Card>
              </>
            )}
          </>
          </div>
        )}
      </div>

      {showAddCandidats && (
        <AjouterCandidatsDialog
          isOpen={true}
          onClose={() => setShowAddCandidats(false)}
          campagneId={campagneId}
          campagneNom={campagne.nom}
          anonyme={campagne.anonyme}
        />
      )}

      <Dialog
        isOpen={showEmailModal}
        onClose={() => {
          setShowEmailModal(false);
          setSelectedParcoursIds([]);
        }}
        title="Envoyer les liens par email"
        className="max-w-2xl"
      >
        <div className="space-y-4">
          {stats && stats.parcours.length > 0 && (
            <>
              <div className="flex items-center justify-between">
                <p className="text-sm text-muted-foreground">
                  {selectedParcoursIds.length} candidat(s) sélectionné(s) sur{" "}
                  {stats.parcours.length}
                </p>
                <Button variant="ghost" size="sm" onClick={toggleAllParcours}>
                  {selectedParcoursIds.length === stats.parcours.length
                    ? "Tout désélectionner"
                    : "Tout sélectionner"}
                </Button>
              </div>

              <div className="max-h-[400px] overflow-y-auto border rounded-md">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-[50px]"></TableHead>
                      <TableHead>Candidat</TableHead>
                      <TableHead>Email</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {stats.parcours.map((p) => (
                      <TableRow
                        key={p._id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => toggleParcours(p._id)}
                      >
                        <TableCell>
                          <Checkbox
                            checked={selectedParcoursIds.includes(p._id)}
                            onCheckedChange={() => toggleParcours(p._id)}
                          />
                        </TableCell>
                        <TableCell className="font-medium">
                          {p.prenomCandidat} {p.nomCandidat}
                        </TableCell>
                        <TableCell>{p.emailCandidat}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEmailModal(false);
                    setSelectedParcoursIds([]);
                  }}
                >
                  Annuler
                </Button>
                <Button
                  disabled={selectedParcoursIds.length === 0 || sendingEmails}
                  onClick={handleSendEmails}
                >
                  {sendingEmails ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Envoi en cours...
                    </>
                  ) : (
                    <>
                      <Mail className="h-4 w-4 mr-2" />
                      Envoyer ({selectedParcoursIds.length})
                    </>
                  )}
                </Button>
              </div>
            </>
          )}
        </div>
      </Dialog>
    </div>
  );
}
